import { BaseProperty, InternalProperty } from '@/features/property/types';
import { useState } from 'react';
import { exportPropertiesToExcel } from '../utils/propertyExporter';
import { RealtimeChartDataResponse } from '@/features/realtimeProperty/api/fetchRealtimeProperty';


/**
 * Hook for exporting property data to Excel
 */
export const usePropertyExport = () => {
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Exports regular properties data to Excel
   */
  const exportProperties = async (
    properties: InternalProperty[],
    options?: {
      filename?: string;
    }
  ) => {
    if (!properties || properties.length === 0) {
      setError('No properties to export');
      return;
    }

    try {
      setIsExporting(true);
      setError(null);

      const customFilename = options?.filename
        ? `${options.filename}-${new Date().toISOString().slice(0, 10)}`
        : undefined;

      await exportPropertiesToExcel(properties, customFilename);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export properties');
      console.error('Export error:', err);
    } finally {
      setIsExporting(false);
    }
  };

  /**
   * Exports data using an explicitly typed export function
   * Supports any export function as long as it matches its expected signature
   */
  const exportData = async <T, F extends (...args: any[]) => Promise<void>>(
    exportFn: F,
    params: Parameters<F>[0] extends RealtimeChartDataResponse
      ? { chartData: RealtimeChartDataResponse; mainProperty: BaseProperty }
      : Parameters<F>[0],
    options?: {
      filename?: string;
    }
  ) => {
    try {
      setIsExporting(true);
      setError(null);

      const customFilename = options?.filename
        ? `${options.filename}-${new Date().toISOString().slice(0, 10)}`
        : undefined;

      // Type-safe handling of different export function signatures
      if ('chartData' in params && 'mainProperty' in params) {
        // For functions like exportPropertyChartData
        await exportFn(
          params.chartData,
          params.mainProperty,
          customFilename
        );
      } else {
        // For other export functions
        await exportFn(params, customFilename);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export data');
      console.error('Export error:', err);
    } finally {
      setIsExporting(false);
    }
  };

  return {
    isExporting,
    error,
    exportProperties,
    exportData,
  };
};